/**
 * i18n — minimal custom provider for REVO PDF Loader (Feb 2026).
 *
 * No external libraries. ~2 KB gzipped.
 *
 * Resolve order (as per spec §2):
 *   1. ?lang=it|en query param if valid.
 *   2. localStorage[revo_locale] if present and valid.
 *   3. navigator.language — "it" for italian, else "en".
 *   4. default "it".
 *
 * Persists the chosen locale in localStorage so navigation keeps it.
 * Updates document.documentElement.lang on every change.
 * Fallback to italian for missing keys (never shows the key name).
 */
import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { it as dictIT } from "./it";
import { en as dictEN } from "./en";

const DICTS = { it: dictIT, en: dictEN };
const SUPPORTED = ["it", "en"];
const DEFAULT = "it";
const STORAGE_KEY = "revo_locale";

function resolveInitialLocale() {
  if (typeof window === "undefined") return DEFAULT;
  try {
    const params = new URLSearchParams(window.location.search);
    const urlLang = (params.get("lang") || "").toLowerCase();
    if (SUPPORTED.includes(urlLang)) {
      localStorage.setItem(STORAGE_KEY, urlLang);
      return urlLang;
    }
  } catch (_) {}
  try {
    const stored = (localStorage.getItem(STORAGE_KEY) || "").toLowerCase();
    if (SUPPORTED.includes(stored)) return stored;
  } catch (_) {}
  try {
    const nav = (navigator.language || "").toLowerCase();
    if (nav.startsWith("it")) return "it";
    return "en";
  } catch (_) {
    return DEFAULT;
  }
}

function lookup(dict, key) {
  // Dot-path lookup e.g. "login.title".
  const parts = key.split(".");
  let cur = dict;
  for (const p of parts) {
    if (cur && typeof cur === "object" && p in cur) cur = cur[p];
    else return undefined;
  }
  return typeof cur === "string" ? cur : undefined;
}

function interpolate(str, vars) {
  if (!vars) return str;
  return str.replace(/\{(\w+)\}/g, (_, k) => (k in vars ? String(vars[k]) : `{${k}}`));
}

const LocaleContext = createContext({ locale: DEFAULT, setLocale: () => {}, t: (k) => k });

export function LocaleProvider({ children }) {
  const [locale, setLocaleState] = useState(resolveInitialLocale);

  useEffect(() => {
    try {
      document.documentElement.lang = locale;
      localStorage.setItem(STORAGE_KEY, locale);
    } catch (_) {}
  }, [locale]);

  const setLocale = useCallback((next) => {
    const n = (next || "").toLowerCase();
    if (SUPPORTED.includes(n)) setLocaleState(n);
  }, []);

  const t = useCallback(
    (key, vars) => {
      const primary = lookup(DICTS[locale] || {}, key);
      if (primary !== undefined) return interpolate(primary, vars);
      // Fallback IT, never emit the raw key
      const fallback = lookup(DICTS[DEFAULT] || {}, key);
      if (fallback !== undefined) return interpolate(fallback, vars);
      // Last resort: last path segment humanized
      return key.split(".").pop() || "";
    },
    [locale]
  );

  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  return useContext(LocaleContext);
}

/** Shorthand hook returning the translator only. */
export function useT() {
  return useContext(LocaleContext).t;
}

export const SUPPORTED_LOCALES = SUPPORTED;
